import { Component, inject, Inject, Input, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormResponse } from 'src/core/interfaces/formPreview.interface';
import { InputGeneratorComponent } from '../input-generator/input-generator.component';
import { PrecreditToogleComponent } from '../precredit-toogle/precredit-toogle.component';
import { CheckboxComponent } from "../checkbox/checkbox.component";
import { TabsComponent } from '../tabs/tabs.component';
import { TabComponent } from '../tab/tab.component';
import { PhoneInputComponent } from '../phone-input/phone-input.component';
import { FlowFormResponse } from 'src/core/interfaces/flowForms.interfaces';
import { isPhoneField } from 'src/core/interfaces/phone.interface';
import { CompanyProfileStore } from 'src/core/stores/company-profile.store';
import { FlowFormsService } from 'src/core/services/flow-forms/flow-forms.service';
import { finalize } from 'rxjs';
import { EmptyStateCardComponent } from '../empty-state-card/empty-state-card.component';
import { ToastService } from 'src/utils/toast.service';
import { FieldLabelPipe } from 'src/ui/pipes/field-label.pipe';

@Component({
  selector: 'form-previewer',
  imports: [CommonModule, ReactiveFormsModule, InputGeneratorComponent, PrecreditToogleComponent, CheckboxComponent, TabsComponent, TabComponent, EmptyStateCardComponent, PhoneInputComponent, FieldLabelPipe],
  templateUrl: './form-previewer.component.html'
})
export class FormPreviewerComponent implements OnInit {
  @Input() formData: FlowFormResponse[] | null = null;
  @Input() onModalWidthChange?: (width: string) => void;
  @Input() disableSubmission: boolean = false;
  @Input() showViewToggle: boolean = true;
  @Input() showFormHeader: boolean = true;
  @Input() showSubmitButton: boolean = true;
  @Input() responsive: boolean = false;
  public formGroups: FormGroup[] = [];
  public viewType: string = 'web';
  public isLoading = signal<boolean>(false);
  public individualIncomplete = signal<boolean>(false);
  public legalIncomplete = signal<boolean>(false);
  private readonly toastService = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);
  public toggleOptionWeb = {
    label: 'Vista Web',
    logo: 'fi fi-rr-computer',
    value: 'web'
  }
  public toggleOptionMobile = {
    label: 'Vista Móvil',
    logo: 'fi fi-rr-smartphone',
    value: 'mobile'
  }
  private productId: string = '';
  private readonly flowFormsService = inject(FlowFormsService);
  private readonly companyProfileStore = inject(CompanyProfileStore);

  @ViewChildren(PhoneInputComponent)
  private phoneInputs!: QueryList<PhoneInputComponent>;

  constructor(private fb: FormBuilder) { }

  isPhoneField(field: any): boolean {
    return isPhoneField(field);
  }

  getTenantIsoCode(): string {
    return this.companyProfileStore.getCountryCode();
  }

  ngOnInit(): void {
    this.productId = sessionStorage.getItem('flowProductId') || '';

    if (!this.formData) {
      if (this.productId) {
        this.getFlowForms();
      }
    } else {
      this.buildForms();
    }
  }

  getFlowForms(): void {
    if (!this.productId) {
      return;
    }

    const productIdNumber = parseInt(this.productId, 10);
    if (isNaN(productIdNumber)) {
      return;
    }

    this.isLoading.set(true);
    this.flowFormsService.getFlowFormsByProductId(productIdNumber).pipe(
      finalize(() => {
        this.isLoading.set(false);
      })
    ).subscribe({
      next: (forms) => {
        this.formData = forms;
        this.buildForms();
        
        // Validar formularios
        this.flowFormsService.validateForm(this.productId).subscribe({
          next: (validationResponse) => {
            // Verificar si hay errores
            if (validationResponse.errors && validationResponse.errors.length > 0) {
              // Filtrar errores de campos obligatorios faltantes
              const mandatoryFieldErrors = validationResponse.errors.filter(
                error => error.code === 'FLOW_FORM_MANDATORY_FIELDS_MISSING'
              );
              
              // Iterar sobre los errores filtrados
              mandatoryFieldErrors.forEach(error => {
                if (error.errors && Array.isArray(error.errors)) {
                  // Verificar si hay errores para persona individual
                  const hasIndividualError = error.errors.some(
                    e => e.applicantType === 'individual'
                  );
                  if (hasIndividualError) {
                    this.individualIncomplete.set(true);
                  }
                  
                  // Verificar si hay errores para persona jurídica
                  const hasLegalEntityError = error.errors.some(
                    e => e.applicantType === 'legal_entity'
                  );
                  if (hasLegalEntityError) {
                    this.legalIncomplete.set(true);
                  }
                }
              });
            }
          },
          error: (error) => {
            console.error('Error validating forms:', error);
          }
        });
      },
      error: (error) => {
        this.toastService.showToast(
          'fi fi-sr-triangle-warning text-[24px] text-negative',
          'No se pudo cargar el formulario. Inténtalo nuevamente.',
          'error'
        );
      }
    });
  }

  buildForms(): void {
    this.formGroups = [];
    this.formData?.forEach((form) => {
      this.formGroups.push(this.buildForm(form));
    });
  }

  buildForm(form: FlowFormResponse): FormGroup {
    const formControls: { [key: string]: any } = {};

    form.sections.forEach(section => {
      section.fields.forEach(field => {
        let validators: any[] = [];
        if (field.isMandatory || field.required) {
          validators = field.dataType === 'BOOLEAN'
            ? [Validators.requiredTrue]
            : [Validators.required];
        }
        const initialValue = field.dataType === 'BOOLEAN' ? false : null;
        formControls[field.fieldId] = [initialValue, validators];
      });
    });

    form.footer?.forEach((footerItem) => {
      if (footerItem.type === 'consent') {
        const validators = footerItem.required ? [Validators.requiredTrue] : [];
        formControls[`consent_${footerItem.order}`] = [false, validators];
      }
    });

    return this.fb.group(formControls);
  }

  getControl(formIndex: number, fieldId: string) {
    return this.formGroups[formIndex]?.get(fieldId);
  }

  getSortedFields(fields: any[]) {
    return [...fields].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  getSortedFooter(footerItems: any[]) {
    return [...footerItems].sort((a, b) => a.order - b.order);
  }

  shouldOccupyFullRow(field: any, fields: any[]): boolean {
    if (field.metadata?.isExtended) {
      return true;
    }
    const sortedFields = this.getSortedFields(fields);
    const isLastField = sortedFields[sortedFields.length - 1].fieldId === field.fieldId;

    // Count total spaces: completeRow fields count as 2, normal fields as 1
    const totalSpaces = sortedFields.reduce((total, f) => {
      return total + (f.metadata?.isExtended ? 2 : 1);
    }, 0);

    const isOddCount = totalSpaces % 2 !== 0;
    return isLastField && isOddCount;
  }

  async onSubmit(formIndex: number): Promise<void> {
    if (this.disableSubmission) {
      return;
    }
    const formGroup = this.formGroups[formIndex];
    if (!formGroup.valid) {
      formGroup.markAllAsTouched();
      return;
    }

    const phones = this.phoneInputs?.toArray() ?? [];
    if (phones.length > 0) {
      const results = await Promise.all(phones.map((p) => p.validateRemote()));
      if (results.some((r) => !r.valid)) {
        formGroup.markAllAsTouched();
      }
    }
  }

  onTypeChange(viewType: any): void {
    this.viewType = viewType;
    if (this.onModalWidthChange) {
      const newWidth = viewType === 'mobile' ? '438px' : '80%';
      this.onModalWidthChange(newWidth);
    }
    // Limpiar todos los formularios al cambiar de vista
    this.formGroups.forEach(formGroup => {
      formGroup.reset();
    });
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

}
